import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect } from "vue"

const _hoisted_1 = { class: "h-full w-full flex justify-center overflow-y-auto no-scrollbar xl:pt-[65px]" }
const _hoisted_2 = { class: "mt-6 mx-4 flex flex-col w-full md:w-[812px]" }
const _hoisted_3 = { class: "bg-white rounded-[20px] border border-ternary h-[584px] md:h-auto pt-6" }
const _hoisted_4 = {
  key: 0,
  class: "px-10 pb-10 font-medium text-[40px] leading-100p text-brown text-left hidden md:block"
}
const _hoisted_5 = { class: "grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-10 px-4 md:px-10 md:pb-10" }
const _hoisted_6 = { class: "flex flex-col items-start" }
const _hoisted_7 = {
  for: "date",
  class: "label-styles"
}
const _hoisted_8 = { class: "flex flex-col items-start" }
const _hoisted_9 = {
  for: "time",
  class: "label-styles"
}
const _hoisted_10 = { class: "flex flex-col items-start" }
const _hoisted_11 = {
  for: "system",
  class: "label-styles"
}
const _hoisted_12 = ["value"]
const _hoisted_13 = { class: "flex flex-col items-start" }
const _hoisted_14 = {
  for: "type",
  class: "label-styles"
}
const _hoisted_15 = ["value"]
const _hoisted_16 = { class: "flex flex-col items-start" }
const _hoisted_17 = {
  for: "city",
  class: "label-styles"
}
const _hoisted_18 = { class: "xl:border-t border-ternary flex items-center justify-center py-6" }
const _hoisted_19 = { class: "py-6" }

import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import GradientButton from "@/components/buttons/GradientButton.vue";
import { fetchQiMenChar } from "@/api/calculators";
import { QiMenChartResponse } from "@/types/calculators";
import DatePicker from "@/components/inputs/DatePicker.vue";
import { formatLocalDate } from "@/utils/date";
import QiMenResults from "@/views/Calculators/QiMen/components/QiMenResults.vue";
import PageTitleWithBackButton from "@/components/PageTitleWithBackButton.vue";
import { useGoogleAutocomplete } from "@/utils/useGoogleAutocomplete";
import GooglePlaceAutocompleteInput from "@/components/inputs/GooglePlaceAutocompleteInput.vue";
import { PlaceSelectedPayload } from "@/types/google-places";
import AccordionFaq from "@/components/accordion/AccordionFAQ.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'QiMenView',
  setup(__props) {

const date = ref<Date | null>(null);
const time = ref<string | null>(null);
const system = ref<string | null>(null);
const matrixType = ref<string | null>(null);
const latitude = ref<number | null>(null);
const longitude = ref<number | null>(null);
const timeZone = ref<string | null>(null);
const qimenChart = ref<QiMenChartResponse | null>(null);
const isFieldsCorrect = computed(
  () =>
    date.value &&
    time.value &&
    system.value &&
    matrixType.value &&
    latitude.value &&
    longitude.value &&
    timeZone.value
);

const matrixTypeLabel = ref<string | null>(null);
const systemLabel = ref<string | null>(null);

const waitApiLoad = ref<boolean>(false);

const { t } = useI18n();
const { normalizeGoogleTimeZone } = useGoogleAutocomplete("ru");

const systemDropdownOptions = [
  { label: "calculators.qi-men.system-dropdown.chai-bu", value: "chai_bu" },
  { label: "calculators.qi-men.system-dropdown.chi-ren", value: "chi_run" },
];
const matrixTypeDropdownOptions = [
  { label: "calculators.qi-men.matrix-type.hourly", value: "hourly" },
  { label: "calculators.qi-men.matrix-type.daily", value: "daily" },
  { label: "calculators.qi-men.matrix-type.monthly", value: "monthly" },
  { label: "calculators.qi-men.matrix-type.yearly", value: "yearly" },
];

function handlePlaceSelected(payload: PlaceSelectedPayload) {
  latitude.value = payload.location.lat();
  longitude.value = payload.location.lng();
  timeZone.value = normalizeGoogleTimeZone(payload.timeZone);
}

async function calculateQiMen() {
  if (waitApiLoad.value) {
    return;
  }
  if (!isFieldsCorrect.value) {
    return;
  }
  systemLabel.value =
    systemDropdownOptions.find((s) => s.value === system.value)?.label ?? "";
  matrixTypeLabel.value =
    matrixTypeDropdownOptions.find((m) => m.value === matrixType.value)
      ?.label ?? "";
  waitApiLoad.value = true;
  try {
    qimenChart.value = await fetchQiMenChar(
      formatLocalDate(date.value as Date),
      time.value as string,
      system.value as string,
      matrixType.value as string,
      latitude.value as number,
      longitude.value as number,
      timeZone.value as string
    );
  } finally {
    waitApiLoad.value = false;
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(PageTitleWithBackButton, {
        "page-title": _unref(t)('calculators.qi-men.title'),
        "title-align": 'left'
      }, null, 8, ["page-title"]),
      (qimenChart.value)
        ? (_openBlock(), _createBlock(QiMenResults, {
            key: 0,
            "qimen-chart": qimenChart.value,
            date: _unref(formatLocalDate)(date.value),
            time: time.value,
            system: systemLabel.value,
            "matrix-type": matrixTypeLabel.value
          }, null, 8, ["qimen-chart", "date", "time", "system", "matrix-type"]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_3, [
        (!qimenChart.value)
          ? (_openBlock(), _createElementBlock("h1", _hoisted_4, _toDisplayString(_unref(t)("calculators.qi-men.title")), 1))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("label", _hoisted_7, _toDisplayString(_unref(t)("calculators.qi-men.labels.date")), 1),
            _createVNode(DatePicker, {
              id: "date",
              onDateSelected: _cache[0] || (_cache[0] = (newDate) => (date.value = newDate))
            })
          ]),
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("label", _hoisted_9, _toDisplayString(_unref(t)("calculators.qi-men.labels.time")), 1),
            _withDirectives(_createElementVNode("input", {
              id: "time",
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((time).value = $event)),
              type: "time",
              class: "border border-divider pl-4 pr-[6px] w-full h-[52px] bg-secondary rounded-[14px] sf-pro font-normal text-base text-muted"
            }, null, 512), [
              [_vModelText, time.value]
            ])
          ]),
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("label", _hoisted_11, _toDisplayString(_unref(t)("calculators.qi-men.labels.system")), 1),
            _withDirectives(_createElementVNode("select", {
              id: "system",
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((system).value = $event)),
              class: "border border-divider pl-4 pr-4 w-full h-[52px] bg-secondary rounded-[14px] sf-pro font-normal text-base text-muted cursor-pointer"
            }, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(systemDropdownOptions, (item) => {
                return _createElementVNode("option", {
                  key: item.value,
                  value: item.value
                }, _toDisplayString(_unref(t)(item.label)), 9, _hoisted_12)
              }), 64))
            ], 512), [
              [_vModelSelect, system.value]
            ])
          ]),
          _createElementVNode("div", _hoisted_13, [
            _createElementVNode("label", _hoisted_14, _toDisplayString(_unref(t)("calculators.qi-men.labels.matrix-type")), 1),
            _withDirectives(_createElementVNode("select", {
              id: "type",
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((matrixType).value = $event)),
              class: "border border-divider pl-4 pr-4 w-full h-[52px] bg-secondary rounded-[14px] sf-pro font-normal text-base text-muted cursor-pointer"
            }, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(matrixTypeDropdownOptions, (item) => {
                return _createElementVNode("option", {
                  key: item.value,
                  value: item.value
                }, _toDisplayString(_unref(t)(item.label)), 9, _hoisted_15)
              }), 64))
            ], 512), [
              [_vModelSelect, matrixType.value]
            ])
          ]),
          _createElementVNode("div", _hoisted_16, [
            _createElementVNode("label", _hoisted_17, _toDisplayString(_unref(t)("labels.city_of_birth")), 1),
            _createVNode(GooglePlaceAutocompleteInput, { onPlaceSelected: handlePlaceSelected })
          ])
        ]),
        _createElementVNode("div", _hoisted_18, [
          _createVNode(GradientButton, {
            class: "w-[191px] h-[56px]",
            "bg-color": "bg-brown-gradient",
            "hover-bg-color": "bg-orange-gradient",
            text: "Рассчитать",
            onClick: calculateQiMen,
            "is-loading": waitApiLoad.value,
            "is-disabled": !isFieldsCorrect.value
          }, null, 8, ["is-loading", "is-disabled"])
        ])
      ]),
      _createElementVNode("div", _hoisted_19, [
        _createVNode(AccordionFaq, {
          faqTemplateName: "faq.qi-men.content",
          title: "FAQ",
          collapseButtonText: _unref(t)('faq.qi-men.collapse-button')
        }, null, 8, ["collapseButtonText"])
      ])
    ])
  ]))
}
}

})